/**
 * Point History Render
 * Handles DOM rendering and display logic for point history
 */

import commonUtil from "../../util/common-utils";
import { Roles } from "../../constants/roles";
import { translator } from "../../translation/translator";
import { timestampHelper } from "../../core/timestamp/timestamp";

interface PointHistoryItem {
    point_date?: unknown;
    rule_type?: unknown;
    rule_name_ja?: unknown;
    rule_name_en?: unknown;
    rule_name_th?: unknown;
    general_description?: unknown;
    description_ja?: unknown;
    description_en?: unknown;
    description_th?: unknown;
    is_completed?: unknown;
    delete_flag?: unknown;
    last_calculated_at?: unknown;
    updated_at?: unknown;
    created_at?: unknown;
    points_earned?: unknown;
    point_history_id?: unknown;
    [key: string]: unknown;
}

type PointHistoryGrouped = Record<string, PointHistoryItem[]>;

export class PointHistoryRender {
    public currentLang: string;

    public constructor() {
        this.currentLang = translator.getCurrentLanguage();
    }

    public renderPointHistory(
        contentEl: HTMLElement,
        histories: PointHistoryItem[],
        options: { canManagePoints?: boolean } = {},
    ): void {
        this.currentLang = translator.getCurrentLanguage();

        if (!histories || histories.length === 0) {
            contentEl.innerHTML = this.buildEmptyStateHTML();
            return;
        }

        const grouped = this.groupByDate(histories);
        const html = this.buildHistoryHTML(grouped, options);
        contentEl.innerHTML = html;
    }

    public groupByDate(histories: PointHistoryItem[]): PointHistoryGrouped {
        const grouped: PointHistoryGrouped = {};
        histories.forEach((history) => {
            const date = String(history.point_date || "");
            if (!grouped[date]) {
                grouped[date] = [];
            }
            grouped[date].push(history);
        });

        Object.keys(grouped).forEach((date) => {
            grouped[date].sort((left, right) => this.compareHistoryItems(right, left));
        });

        return grouped;
    }

    public buildHistoryHTML(grouped: PointHistoryGrouped, options: { canManagePoints?: boolean } = {}): string {
        let html = '<div class="point-history-list">';

        Object.keys(grouped)
            .sort((a, b) => b.localeCompare(a))
            .forEach((date) => {
                const items = grouped[date];

                html += `
                <div class="point-history-date-group">
                    <div class="point-history-table" role="table" aria-label="Point history for ${this.formatDate(date)}">
            `;

                items.forEach((item) => {
                    html += this.buildHistoryItemHTML(item, options);
                });

                html += `
                    </div>
                </div>
            `;
            });

        html += "</div>";
        return html;
    }

    public buildHistoryItemHTML(item: PointHistoryItem, options: { canManagePoints?: boolean } = {}): string {
        const ruleName =
            item[`rule_name_${this.currentLang}`] ||
            item.rule_name_ja ||
            item.rule_name_en ||
            item.rule_name_th ||
            this.formatRuleType(String(item.rule_type || ""));
        const pointHistoryDescription =
            item.general_description ||
            item[`description_${this.currentLang}`] ||
            item.description_ja ||
            item.description_en ||
            item.description_th ||
            "";
        const isCompleted = Boolean(item.is_completed);
        const isInvalid = Number(item.delete_flag ?? 0) === 1;
        const timeString = item.last_calculated_at || item.updated_at || item.created_at;
        const updatedAt = this.formatAbsoluteDateTime(String(timeString || ""));
        const safeRuleName = commonUtil.escapeHtml(String(ruleName || "-"));
        const safeUpdatedAt = commonUtil.escapeHtml(String(updatedAt || "-"));
        const points = Number(item.points_earned ?? 0);
        const pointText = `${points > 0 ? "+" : ""}${this.formatPointValue(points)}`;
        const safePoints = commonUtil.escapeHtml(pointText);
        const safePointHistoryDescription = commonUtil.escapeHtml(String(pointHistoryDescription || ""));
        const pointHistoryId = Number(item.point_history_id ?? 0);
        const safeRuleNameForData = commonUtil.escapeHtml(String(ruleName || "-"));

        return `
            <div class="point-history-table__row ${isCompleted ? "completed" : "incomplete"} ${isInvalid ? "is-invalid" : ""}" role="row">
                <div class="point-history-table__label" role="rowheader">
                    <div class="point-history-table__label-inner">
                        <i class="bi ${this.getRuleIcon(String(item.rule_type || ""))} history-item-icon" aria-hidden="true"></i>
                        <div class="point-history-table__label-body">
                            <div class="history-item-title info-label">
                                ${safeRuleName}
                                ${isInvalid ? '<span class="point-history-status-badge">Invalid</span>' : ""}
                                ${safePointHistoryDescription ? `<i class="bi bi-info-circle history-item-info" title="${safePointHistoryDescription}"></i>` : ""}
                            </div>
                            <div class="history-item-meta">
                                <span class="history-item-time"><i class="bi bi-clock"></i> ${safeUpdatedAt}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="point-history-table__value" role="cell">
                    <div class="info-value">
                        <span class="points-earned ${points < 0 ? "is-negative" : ""}"><img src="/images/points/coin.png" class="coin-img" alt="" aria-hidden="true"><span>${safePoints} pts</span></span>
                        ${options.canManagePoints && !isInvalid && pointHistoryId > 0 ? `<button type="button" class="btn delete point-history-row-action" data-role="${Roles.POINT_HISTORY_INVALIDATE_BUTTON}" data-point-history-invalidate="${pointHistoryId}" data-point-history-amount-text="${safePoints}" data-point-history-rule-name="${safeRuleNameForData}" data-translate="invalidate">Invalidate</button>` : ""}
                    </div>
                </div>
            </div>
        `;
    }

    public buildEmptyStateHTML(): string {
        return `
            <div class="point-history-table" role="table" aria-label="Point history">
                <div class="point-history-table__row" role="row">
                    <div class="point-history-table__label" role="rowheader"><span class="info-label">History</span></div>
                    <div class="point-history-table__value" role="cell"><span class="info-value">No history found</span></div>
                </div>
            </div>
        `;
    }

    public formatDate(dateString: string): string {
        // point_date is a plain calendar date (no time-of-day/offset), e.g. "2026-07-21" —
        // parsed by JS as UTC midnight. Format in UTC (not the viewer's timeZone) so the
        // displayed day never shifts backward/forward for viewers behind/ahead of UTC.
        const date = timestampHelper.toDate(dateString);
        if (!date) return dateString;
        try {
            return new Intl.DateTimeFormat(timestampHelper.getIntlLocale(), {
                timeZone: "UTC",
                year: "numeric",
                month: "short",
                day: "numeric",
            }).format(date);
        } catch {
            return dateString;
        }
    }

    public formatAbsoluteDateTime(dateStr: string): string {
        if (!dateStr) return "-";
        const date = timestampHelper.toDate(dateStr);
        if (!date) return dateStr;
        try {
            return new Intl.DateTimeFormat(timestampHelper.getIntlLocale(), {
                timeZone: timestampHelper.getTimeZone() || undefined,
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }).format(date);
        } catch {
            return dateStr;
        }
    }

    public getRuleIcon(ruleType: string): string {
        const iconMap: Record<string, string> = {
            daily_login: "bi-box-arrow-in-right",
            read_article: "bi-newspaper",
            review_100_200: "bi-pencil-square",
            review_300_plus: "bi-file-text",
            review_with_photo: "bi-image",
            review_with_video: "bi-camera-video",
            share_on_sns: "bi-share",
            like_post: "bi-heart",
            receive_like: "bi-heart",
            comment_post: "bi-chat-left-text",
            receive_comment: "bi-chat-left-dots",
            manual_adjustment: "bi-sliders",
            manual: "bi-sliders",
            adjustment: "bi-sliders",
            redeem: "bi-arrow-left-right",
        };
        return iconMap[ruleType] || "bi-coin";
    }

    public formatRuleType(ruleType: string | undefined): string {
        if (!ruleType) {
            return "-";
        }

        return String(ruleType)
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    public formatPointValue(value: number): string {
        if (!Number.isFinite(value)) {
            return "0";
        }

        if (Number.isInteger(value)) {
            return String(value);
        }

        return value
            .toFixed(2)
            .replace(/\.00$/, "")
            .replace(/(\.\d*[1-9])0+$/, "$1");
    }

    public compareHistoryItems(left: PointHistoryItem, right: PointHistoryItem): number {
        const leftTime = this.toTimestamp(left?.last_calculated_at || left?.updated_at || left?.created_at || "");
        const rightTime = this.toTimestamp(right?.last_calculated_at || right?.updated_at || right?.created_at || "");

        if (leftTime !== rightTime) {
            return leftTime - rightTime;
        }

        const leftId = Number(left?.point_history_id ?? 0);
        const rightId = Number(right?.point_history_id ?? 0);
        return leftId - rightId;
    }

    public toTimestamp(value: unknown): number {
        const timestamp = Date.parse(String(value || ""));
        return Number.isFinite(timestamp) ? timestamp : 0;
    }
}
